// The interface language: the one place a future language switch hooks in. The app speaks English only today,
// so every request falls back to "en". Asked for, in order: `?lang=` in the address, localStorage["fs.locale"],
// then the browser's languages. The result lands on <html lang>, which is also how the docs screenshot run
// (apps/site/scripts/docs-shots.ts) tells whether the app switched language.

/** Languages the interface has. Add one here once its strings exist. */
export const SUPPORTED_LOCALES: readonly string[] = ["en"];
export const DEFAULT_LOCALE = "en";
export const LOCALE_STORAGE = "fs.locale";

export interface LocaleAsk {
  /** the value of `?lang=` */
  query?: string | null;
  /** localStorage["fs.locale"] */
  stored?: string | null;
  /** navigator.languages (or [navigator.language]) */
  browser?: readonly string[];
}

/** "nl-BE" → "nl"; null for anything that is not a language tag. */
export function baseLanguage(tag: string | null | undefined): string | null {
  const m = /^([a-z]{2,3})(?:[-_][a-z0-9]{1,8})*$/i.exec(tag?.trim() ?? "");
  return m ? m[1]!.toLowerCase() : null;
}

/** The first asked-for language the interface has; the default when none is. Pure. */
export function pickLocale(ask: LocaleAsk, supported: readonly string[] = SUPPORTED_LOCALES): string {
  for (const tag of [ask.query, ask.stored, ...(ask.browser ?? [])]) {
    const lang = baseLanguage(tag);
    if (lang && supported.includes(lang)) return lang;
  }
  return DEFAULT_LOCALE;
}

/** What `applyLocale` touches; the browser's `window` fits it. */
export interface LocaleWindow {
  location: { search: string };
  navigator: { languages?: readonly string[]; language?: string };
  document: { documentElement: { lang: string } };
  localStorage?: Pick<Storage, "getItem">;
}

/** Picks the language and sets <html lang>. Returns it. */
export function applyLocale(win: LocaleWindow = window): string {
  let stored: string | null = null;
  try {
    stored = win.localStorage?.getItem(LOCALE_STORAGE) ?? null;
  } catch {
    // blocked storage: the other signals still count
  }
  const browser = win.navigator.languages?.length
    ? win.navigator.languages
    : win.navigator.language
      ? [win.navigator.language]
      : [];
  const locale = pickLocale({ query: new URLSearchParams(win.location.search).get("lang"), stored, browser });
  win.document.documentElement.lang = locale;
  return locale;
}

/** The language `applyLocale` picked (read back from <html lang>); the default before it ran. */
export function currentLocale(
  doc: { documentElement: { lang: string } } | undefined = globalThis.document,
): string {
  return doc?.documentElement.lang || DEFAULT_LOCALE;
}
